import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";

/**
 * ⌘K Command Palette — the Linear/Notion/Stripe signature, built on the v2
 * motion stack. Scoped to the styleguide as a real, working interaction demo.
 *
 * - ⌘K / Ctrl+K opens; Esc closes; typing filters; ↑/↓ moves; Enter runs.
 * - `globalShortcut` (default true) owns that window-level ⌘K. Pass false where a real global
 *   palette is already mounted above this page — the authenticated AppShell mounts the production
 *   `ft-cmdk`, and two listeners on one keystroke would open two dialogs. The trigger button and the
 *   whole demo interaction stay exactly as they are; only the shortcut stands down.
 * - motion + AnimatePresence for spring enter/exit; reduced-motion → instant.
 * - Focus is trapped to the input; focus returns to the trigger on close.
 * - Styling comes from .sgv2__cmdk-* in styleguide-v2.scss (v2 tokens).
 */

const ACTIONS = [
  { id: "new-listing", label: "New listing", hint: "Create a listing", group: "Create", icon: "+", keys: "⌘N" },
  { id: "new-lookbook", label: "New lookbook", hint: "Start a lookbook", group: "Create", icon: "+" },
  { id: "go-listings", label: "Go to Listings", hint: "Manage inventory", group: "Navigate", icon: "→", keys: "G L" },
  { id: "go-orders", label: "Go to Orders", hint: "Open the orders board", group: "Navigate", icon: "→", keys: "G O" },
  { id: "go-requests", label: "Go to Requests", hint: "Buy & sell requests", group: "Navigate", icon: "→", keys: "G R" },
  { id: "go-pricing", label: "Go to Price review", hint: "Review flagged prices", group: "Navigate", icon: "→" },
  { id: "filter-active", label: "Filter: Active only", hint: "Show active listings", group: "Filter", icon: "⌕" },
  { id: "filter-consignment", label: "Filter: Consignment", hint: "Consignment stock", group: "Filter", icon: "⌕" },
  { id: "toggle-density", label: "Toggle compact density", hint: "Fit more rows", group: "View", icon: "▤", keys: "⌘D" },
  { id: "help", label: "Keyboard shortcuts", hint: "Show all shortcuts", group: "Help", icon: "?", keys: "?" },
];
const GROUP_ORDER = ["Create", "Navigate", "Filter", "View", "Help"];

// Cheap fuzzy: every query char appears in order in the label (case-insensitive).
const fuzzy = (q, s) => {
  if (!q) return true;
  q = q.toLowerCase();
  s = s.toLowerCase();
  let i = 0;
  for (const ch of s) if (ch === q[i]) i++;
  return i === q.length;
};

const CommandPalette = ({ onRun, globalShortcut = true }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  const results = useMemo(
    () => ACTIONS.filter((a) => fuzzy(query, a.label) || fuzzy(query, a.hint)),
    [query]
  );

  // Group the flat results by section, preserving the flat index each row holds
  // (arrow-key nav / active state runs on the flat `results` array).
  const groups = useMemo(() => {
    const byGroup = new Map();
    results.forEach((a, flatIndex) => {
      if (!byGroup.has(a.group)) byGroup.set(a.group, []);
      byGroup.get(a.group).push({ ...a, flatIndex });
    });
    return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({ group: g, items: byGroup.get(g) }));
  }, [results]);

  // Global ⌘K / Ctrl+K to open; Esc to close. When `globalShortcut` is false the shortcut belongs to
  // the palette mounted above us: yield it, and close this demo so ⌘K can never show two dialogs.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        if (!globalShortcut) {
          setOpen(false);
          return;
        }
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, globalShortcut]);

  // Reset + focus on open; restore focus to trigger on close.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      // focus after the enter animation frame
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      triggerRef.current?.focus();
    }
  }, [open]);

  // Keep the active row clamped and scrolled into view.
  useEffect(() => {
    if (active >= results.length) setActive(Math.max(0, results.length - 1));
  }, [results.length, active]);

  const run = (a) => {
    if (!a) return;
    setOpen(false);
    onRun?.(a);
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(results[active]);
    }
  };

  const spring = reduce
    ? { duration: 0 }
    : { type: "spring", stiffness: 420, damping: 32 };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="sgv2__cmdk-trigger"
        onClick={() => setOpen(true)}
      >
        <span className="sgv2__cmdk-trigger-icon" aria-hidden="true">⌕</span>
        Search or run a command
        <kbd className="sgv2__cmdk-kbd">⌘K</kbd>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="sgv2__cmdk-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.15 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
          >
            <motion.div
              className="sgv2__cmdk"
              role="dialog"
              aria-modal="true"
              aria-label="Command palette"
              initial={{ opacity: 0, y: reduce ? 0 : -12, scale: reduce ? 1 : 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: reduce ? 0 : -8, scale: reduce ? 1 : 0.98 }}
              transition={spring}
            >
              <span className="sgv2__cmdk-handle" aria-hidden="true" />
              <div className="sgv2__cmdk-inputrow">
                <span className="sgv2__cmdk-inputicon" aria-hidden="true">⌕</span>
                <input
                  ref={inputRef}
                  className="sgv2__cmdk-input"
                  placeholder="Search or run a command…"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                  onKeyDown={onInputKey}
                  aria-activedescendant={results[active] ? `cmdk-${results[active].id}` : undefined}
                  aria-controls="cmdk-list"
                  aria-expanded="true"
                  role="combobox"
                />
                <kbd className="sgv2__cmdk-kbd">Esc</kbd>
              </div>

              <ul className="sgv2__cmdk-list" id="cmdk-list" role="listbox" ref={listRef}>
                {results.length === 0 && (
                  <li className="sgv2__cmdk-empty">
                    <span className="sgv2__cmdk-empty-icon" aria-hidden="true">⌕</span>
                    No commands match “{query}”.
                  </li>
                )}
                {groups.map(({ group, items }) => (
                  <li key={group} className="sgv2__cmdk-group" role="presentation">
                    <p className="sgv2__cmdk-grouphead">{group}</p>
                    <ul className="sgv2__cmdk-grouplist" role="group" aria-label={group}>
                      {items.map((a) => (
                        <li
                          key={a.id}
                          id={`cmdk-${a.id}`}
                          role="option"
                          aria-selected={a.flatIndex === active}
                          className={`sgv2__cmdk-item${a.flatIndex === active ? " sgv2__cmdk-item--active" : ""}`}
                          onMouseMove={() => setActive(a.flatIndex)}
                          onClick={() => run(a)}
                        >
                          <span className="sgv2__cmdk-item-icon" aria-hidden="true">{a.icon}</span>
                          <span className="sgv2__cmdk-item-text">
                            <span className="sgv2__cmdk-item-label">{a.label}</span>
                            {a.hint && <span className="sgv2__cmdk-item-hint">{a.hint}</span>}
                          </span>
                          {a.keys
                            ? <kbd className="sgv2__cmdk-item-keys">{a.keys}</kbd>
                            : <span className="sgv2__cmdk-item-go" aria-hidden="true">↵</span>}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>

              <div className="sgv2__cmdk-foot">
                <span><kbd className="sgv2__cmdk-kbd">↑</kbd><kbd className="sgv2__cmdk-kbd">↓</kbd> navigate</span>
                <span><kbd className="sgv2__cmdk-kbd">↵</kbd> run</span>
                <span><kbd className="sgv2__cmdk-kbd">esc</kbd> close</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CommandPalette;
